import { createInertiaApp, router } from "@inertiajs/react"
import { type ReactNode, createElement } from "react"
import { createRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"
import { apiKeysPath } from "@/routes"

// Import Cesium styles
import "@/styles/cesium.css"

// Set Cesium base URL for loading assets
;(window as any).CESIUM_BASE_URL = "/cesium"

const VALID_ROUTES_TO_REDIRECT_TO = [apiKeysPath()]

router.on("invalid", (event) => {
  const responseUrl = (event.detail.response.request as XMLHttpRequest)
    ?.responseURL

  if (
    VALID_ROUTES_TO_REDIRECT_TO.some((route) => responseUrl.includes(route))
  ) {
    event.preventDefault()
    window.location.href = responseUrl
  }
})

// Temporary type definition, until @inertiajs/react provides one
interface ResolvedComponent {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode }
}

const appName = (import.meta.env.VITE_APP_NAME ?? "Rails") as string

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => `${title} - ${appName}`,

  resolve: async (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx")
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)

      // Guard against undefined page components to avoid TypeError when calling `page()`
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }
    const module = await page()

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    module.default.layout ??= (page) =>
      createElement(PersistentLayout, null, page)

    return module
  },

  setup({ el, App, props }) {
    // Uncomment the following to enable SSR hydration:
    // if (el.hasChildNodes()) {
    //   hydrateRoot(el, createElement(App, props))
    //   return
    // }
    createRoot(el).render(createElement(App, props))
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()
